import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const METHOD_LABELS = { safe: "Kasa", bank: "Banka", temlik: "Temlik", check: "Çek", cek: "Çek", senet: "Senet", credit_card: "Kredi Kartı" };
const PROD_LABELS = { beklemede: "Beklemede", uretimde: "Üretimde", boyahane: "Boyahane", hazir: "Hazır" };
const INST_LABELS = { beklemede: "Montaj Bekleyen", planlandi: "Planlandı", tamamlandi: "Tamamlandı", iptal_edildi: "İptal" };
export const tl = (v) => {
  const n = Number(v || 0);
  return `${n < 0 ? "-" : ""}₺${Math.abs(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
};

const Stat = ({ label, value, tone = "text-gray-900", testId }) => (
  <div className="p-3 rounded-lg border bg-white" data-testid={testId}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-bold ${tone}`}>{value}</p>
  </div>
);

export const PipelineCard = ({ pipeline }) => (
  <Card data-testid="reports-pipeline">
    <CardHeader className="pb-2"><CardTitle className="text-base">Üretim ve Montaj Durumu (şu an)</CardTitle></CardHeader>
    <CardContent className="space-y-3">
      {["lake", "melamin"].map((line) => (
        <div key={line}>
          <p className="text-sm font-semibold mb-1">{line === "lake" ? "Lake Hattı" : "Melamin Hattı"}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(PROD_LABELS).map(([k, label]) => (
              <Stat key={k} label={label} value={pipeline?.production?.[line]?.[k] || 0} testId={`pipeline-${line}-${k}`} />
            ))}
          </div>
        </div>
      ))}
      <div>
        <p className="text-sm font-semibold mb-1">Montaj</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(INST_LABELS).map(([k, label]) => (
            <Stat key={k} label={label} value={pipeline?.installation?.[k] || 0} testId={`pipeline-installation-${k}`} />
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const ReportsOverviewExtra = ({ api, sessionToken, year }) => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cashBank, setCashBank] = useState(null);

  useEffect(() => {
    const params = { session_token: sessionToken, year };
    axios.get(`${api}/reports/overview-extra`, { params }).then((r) => setData(r.data)).catch((e) => console.error(e));
    axios.get(`${api}/dashboard/summary`, { params: { session_token: sessionToken } }).then((r) => setSummary(r.data)).catch((e) => console.error(e));
    axios.get(`${api}/cash-bank-summary`).then((r) => setCashBank(r.data)).catch((e) => console.error(e));
  }, [api, sessionToken, year]);

  if (!data) return null;
  const chartData = data.months.map((m) => ({ ...m, name: MONTHS[m.month - 1] }));
  const methodTotal = data.payment_methods.reduce((s, m) => s + m.amount, 0) || 1;
  const cash = cashBank?.total_cash ?? cashBank?.cash_balance ?? cashBank?.kasa ?? null;
  const bank = cashBank?.total_bank ?? cashBank?.bank_balance ?? cashBank?.banka ?? null;

  return (
    <div className="space-y-6 mt-6" data-testid="reports-overview-extra">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Toplam Alacak (Müşteri)" value={tl(summary?.receivables?.total)} tone="text-green-700" testId="overview-receivables" />
        <Stat label="Toplam Borç (Tedarikçi)" value={tl(summary?.payables?.total)} tone="text-red-700" testId="overview-payables" />
        <Stat label="Net Durum" value={tl((summary?.receivables?.total || 0) - (summary?.payables?.total || 0))} testId="overview-net" />
        <Stat label="Kasa" value={cash == null ? "-" : tl(cash)} tone="text-blue-700" testId="overview-cash" />
        <Stat label="Banka" value={bank == null ? "-" : tl(bank)} tone="text-indigo-700" testId="overview-bank" />
      </div>

      <Card data-testid="overview-monthly-chart">
        <CardHeader className="pb-2"><CardTitle className="text-base">{data.year} Aylık Satış / Tahsilat / Gider</CardTitle></CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                <Tooltip formatter={(v) => tl(v)} />
                <Legend />
                <Bar dataKey="sales" name="Satış" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Bar dataKey="collections" name="Tahsilat (temlik hariç)" fill="#16a34a" radius={[3, 3, 0, 0]} />
                <Bar dataKey="temlik" name="Temlik" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name="Gider" fill="#dc2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="overview-payment-methods">
          <CardHeader className="pb-2"><CardTitle className="text-base">Tahsilat Ödeme Yöntemi Dağılımı ({data.year})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.payment_methods.length === 0 && <p className="text-sm text-gray-500">Kayıt yok</p>}
            {data.payment_methods.map((m) => {
              const pct = Math.round((m.amount / methodTotal) * 1000) / 10;
              return (
                <div key={m.method} data-testid={`payment-method-${m.method}`}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{METHOD_LABELS[m.method] || m.method} <span className="text-gray-400">({m.count} işlem)</span></span>
                    <span>{tl(m.amount)} • %{pct}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1"><div className="bg-blue-600 h-2 rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <PipelineCard pipeline={data.pipeline} />
      </div>
    </div>
  );
};
